import React, { useMemo, useRef, useState } from 'react';
import 'media-chrome';
import { formatAudioDuration } from '../../helpers/audioLesson';

const mediaControllerStyle = {
  width: '100%',
  borderRadius: 10,
  overflow: 'hidden',
  background: '#fff',
  border: '1px solid #dee2e6',
  '--media-primary-color': '#212529',
  '--media-secondary-color': '#6c757d',
  '--media-accent-color': '#007bff',
  '--media-control-background': 'transparent',
  '--media-control-hover-background': '#e9f2ff'
};

const mediaElementStyle = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  display: 'block'
};

const controlBarStyle = {
  gap: '0.4rem',
  padding: '0.5rem'
};

const speedWrapStyle = {
  marginLeft: 'auto',
  display: 'inline-flex',
  alignItems: 'center'
};

const speedSelectStyle = {
  height: '1.9rem',
  minWidth: '3.15rem',
  border: 'none',
  borderRadius: 999,
  padding: '0 1.1rem 0 0.35rem',
  fontSize: '0.9rem',
  fontWeight: 600,
  lineHeight: 1,
  color: '#212529',
  backgroundColor: 'transparent',
  boxShadow: 'none',
  outline: 'none',
  cursor: 'pointer',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2.25 4.5 6 8.25 9.75 4.5' fill='none' stroke='%236c757d' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.18rem center',
  backgroundSize: '0.75rem 0.75rem'
};

const timelineRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.4rem 0.6rem 0.55rem',
  borderTop: '1px solid #e9ecef'
};

const timelineRangeStyle = {
  flex: '1 1 auto',
  minWidth: 120,
  '--media-range-track-background': '#dfe3e8',
  '--media-primary-color': '#2f3540',
  '--media-secondary-color': '#dfe3e8',
  '--media-range-track-border-radius': '999px',
  '--media-range-track-box-shadow': 'inset 0 0 0 1px rgba(0,0,0,0.05)',
  '--media-time-range-buffered-color': '#cbd3dc',
  '--media-range-track-height': '0.28rem',
  '--media-range-track-outline': 'none',
  '--media-control-hover-background': 'transparent',
  '--media-time-range-hover-height': '100%',
  '--media-range-thumb-height': '0.78rem',
  '--media-range-thumb-width': '0.78rem',
  '--media-range-track-pointer-background': '#2f3540'
};

function LessonAudioPlayer({ src, title, durationSeconds, className = '' }) {
  const [loadError, setLoadError] = useState(false);
  const [playbackRate, setPlaybackRate] = useState('1');
  const audioRef = useRef(null);

  const durationLabel = useMemo(() => formatAudioDuration(durationSeconds), [durationSeconds]);

  if (!src) return null;

  return (
    <section
      className={className.trim() || undefined}
      aria-label={title ? `Audio lesson: ${title}` : 'Audio lesson'}
    >
      {loadError ? (
        <div className="alert alert-warning mb-0 small" role="alert">
          This audio could not be loaded. The link may be missing or invalid.
        </div>
      ) : (
        <media-controller audio style={mediaControllerStyle}>
          <audio
            ref={audioRef}
            key={src}
            slot="media"
            src={src}
            preload="metadata"
            style={mediaElementStyle}
            onError={() => setLoadError(true)}
            onCanPlay={() => setLoadError(false)}
          />
          <media-control-bar style={controlBarStyle}>
            <media-play-button />
            <media-seek-backward-button seekoffset="5" />
            <media-seek-forward-button seekoffset="5" />
            <media-mute-button />
            <media-volume-range />
            <span style={speedWrapStyle}>
              <select
                aria-label="Playback speed"
                value={playbackRate}
                style={speedSelectStyle}
                onChange={e => {
                  const next = e.target.value;
                  setPlaybackRate(next);
                  if (audioRef.current) {
                    audioRef.current.playbackRate = Number(next);
                  }
                }}
              >
                <option value="0.5">0.5x</option>
                <option value="1">1x</option>
                <option value="1.25">1.25x</option>
                <option value="1.5">1.5x</option>
                <option value="2">2x</option>
              </select>
            </span>
          </media-control-bar>
          <div style={timelineRowStyle}>
            <media-time-display remaining={false} />
            <media-time-range style={timelineRangeStyle} />
            <media-duration-display />
          </div>
        </media-controller>
      )}
    </section>
  );
}

export default LessonAudioPlayer;
